import { action } from "typesafe-actions";

import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import {
  closeLoaderWithMessage,
  openLoaderWithMessage,
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";

import { uploadFile } from "../../components/FileUpload/utils";
import { getFileType } from "../../helpers/fileTypeDetector";
import { endPoints } from "../../constants/endPoints";
import { ICampaignRuleTemplate, IMarketCampaignBasicInfo, IMarketCampaignChannel, IMarketCampaignDraftStep } from "./marketCampaign.types";


export const FETCH_CAMPAIGN_RULE_TEMPLATES_PROGRESS =
  "FETCH_CAMPAIGN_RULE_TEMPLATES_PROGRESS";
export const FETCH_CAMPAIGN_RULE_TEMPLATES_SUCCESS =
  "FETCH_CAMPAIGN_RULE_TEMPLATES_SUCCESS";
export const FETCH_CAMPAIGN_RULE_TEMPLATES_FAILED =
  "FETCH_CAMPAIGN_RULE_TEMPLATES_FAILED";

export const fetchCampaignRuleTemplatesProgress = () =>
  action(FETCH_CAMPAIGN_RULE_TEMPLATES_PROGRESS);
export const fetchCampaignRuleTemplatesSuccess = (
  list: ICampaignRuleTemplate[]
) => action(FETCH_CAMPAIGN_RULE_TEMPLATES_SUCCESS, { list });
export const fetchCampaignRuleTemplatesFailed = () =>
  action(FETCH_CAMPAIGN_RULE_TEMPLATES_FAILED);

export const fetchCampaignRuleTemplatesAsync =
  (
    company_outlet_uuid: string,
    campaign_rule_template_uuid?: string,

  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(fetchCampaignRuleTemplatesProgress());

        let url = "";
        if (campaign_rule_template_uuid) {
          url += `&campaign_rule_template_uuid=${campaign_rule_template_uuid}`;
        }
        const res = await api.get(
          `${endPoints.crossroadsLeads.getCampaignRuleTemplate}?company_outlet_uuid=${company_outlet_uuid}&pageNo=1&itemPerPage=100${url}`
        );
        const data: ICampaignRuleTemplate[] = res.data.data;

        dispatch(fetchCampaignRuleTemplatesSuccess(data));
      } catch (err: any) {
        dispatch(fetchCampaignRuleTemplatesFailed());
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      }
    };

export const CLEAR_MARKET_CAMPAIGN_RULE_TEMPLATES_STATE = "CLEAR_MARKET_CAMPAIGN_RULE_TEMPLATES_STATE";

export const upsertMarketCampaignChannelAsync =
  (
    data: IMarketCampaignChannel,
    file?: any | null,
    showLoader?: boolean,
    onSuccess?: (data: IMarketCampaignChannel, path?: string) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        if (showLoader) dispatch(saveLoaderProgress());

        let path = "";
        if (file) {
          path = await uploadFile(
            file,
            "WHATSAPP_MARKET_CAMPAIGN",
            data.header_media?.[0]?.value || "",
            {},
          );
        }

        let res;
        if (file) {
          res = await api.post("/campaign/upsert-market-campaign-channel", {
            ...data,
            attachment:
              "https://enr-biolerplate.s3.ap-south-1.amazonaws.com/" + path,
            header_media: [
              {
                type: data.header_media[0].type,
                value:
                  "https://enr-biolerplate.s3.ap-south-1.amazonaws.com/" + path,
              },
            ],
          });
        } else {
          res = await api.post("/campaign/upsert-market-campaign-channel", {
            ...data,
          });
        }

        onSuccess?.(res.data.data, path);
        dispatch(
          showMessage({
            type: "success",
            message: "Campaign channel saved successfully",
            displayAs: "snackbar",
          }),
        );
      } catch (err: any) {
        dispatch(
          showMessage({
            type: "error",
            message:
              err.response?.data?.message || "Failed to save campaign channel",
            displayAs: "snackbar",
          }),
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };


export const upsertDraftStepAsync =
  (
    data: IMarketCampaignDraftStep,
    onSuccess?: (data: IMarketCampaignDraftStep) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(saveLoaderProgress());

        // Get current draft step data from state if it exists
        const currentState = getState();
        const existingDraftStep =
          currentState.marketCampaign.marketCampaignDraftSteps.data;

        // Merge new data with existing data
        const mergedData = {
          ...(existingDraftStep?.draft_step_data || {}), // Existing data
          ...data.draft_step_data, // New data
        };

        const payload = {
          ...data,
          draft_step_data: mergedData,
        };

        const res = await api.post("/campaign/upsert-draft-step", payload);
        dispatch(saveLoaderCompleted());

        dispatch(
          showMessage({
            type: "success",
            message: "Campaign draft step saved successfully",
            displayAs: "snackbar",
          }),
        );

        onSuccess?.(res.data.data);
        return res.data.data;
      } catch (err: any) {
        dispatch(
          showMessage({
            type: "error",
            message:
              err.response?.data?.message || "Failed to save campaign draft step",
            displayAs: "snackbar",
          }),
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };


// Action types for fetching result of campaign SQL query
export const FETCH_CAMPAIGN_SQL_QUERY_RESULT_PROGRESS =
  "FETCH_CAMPAIGN_SQL_QUERY_RESULT_PROGRESS";
export const FETCH_CAMPAIGN_SQL_QUERY_RESULT_SUCCESS =
  "FETCH_CAMPAIGN_SQL_QUERY_RESULT_SUCCESS";
export const FETCH_CAMPAIGN_SQL_QUERY_RESULT_FAILED =
  "FETCH_CAMPAIGN_SQL_QUERY_RESULT_FAILED";

export const fetchCampaignSqlQueryResultProgress = () =>
  action(FETCH_CAMPAIGN_SQL_QUERY_RESULT_PROGRESS);
export const fetchCampaignSqlQueryResultSuccess = (list: any[]) =>
  action(FETCH_CAMPAIGN_SQL_QUERY_RESULT_SUCCESS, { list });
export const fetchCampaignSqlQueryResultFailed = (errorMessage: string) =>
  action(FETCH_CAMPAIGN_SQL_QUERY_RESULT_FAILED, { errorMessage });


export const fetchCampaignSqlQueryResultAsync =
  (
    market_campaign_uuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(fetchCampaignSqlQueryResultProgress());
        const res = await api.get(
          `/campaign/get-result-of-campaign-sql-query?market_campaign_uuid=${market_campaign_uuid}`,
        );
        dispatch(fetchCampaignSqlQueryResultSuccess(res.data.data));

        if (res.data.data.length === 0) {
          dispatch(
            showMessage({
              type: "warning",
              message: "No audience list found!",
              displayAs: "snackbar",
            }),
          );
        }
      } catch (err: any) {
        const message =
          err.response?.data?.message || "Failed to fetch SQL query result";
        dispatch(fetchCampaignSqlQueryResultFailed(message));
        dispatch(
          showMessage({
            type: "error",
            message,
            displayAs: "snackbar",
          }),
        );
      }
    };


export const FETCH_QUICK_SEND_CAMPAIGN_LIST_PROGRESS =
  "FETCH_QUICK_SEND_CAMPAIGN_LIST_PROGRESS";
export const FETCH_QUICK_SEND_CAMPAIGN_LIST_SUCCESS =
  "FETCH_QUICK_SEND_CAMPAIGN_LIST_SUCCESS";
export const FETCH_QUICK_SEND_CAMPAIGN_LIST_FAILED =
  "FETCH_QUICK_SEND_CAMPAIGN_LIST_FAILED";

export const fetchQuickSendCampaignListProgress = () =>
  action(FETCH_QUICK_SEND_CAMPAIGN_LIST_PROGRESS);
export const fetchQuickSendCampaignListSuccess = (
  list: any[],
  headers: any[],
  totalRecords: number,
) =>
  action(FETCH_QUICK_SEND_CAMPAIGN_LIST_SUCCESS, {
    list,
    headers,
    totalRecords,
  });
export const fetchQuickSendCampaignListFailed = () =>
  action(FETCH_QUICK_SEND_CAMPAIGN_LIST_FAILED);



interface IFetchQuickSendCampaignListPayload {
  market_campaign_uuid: string;
  company_outlet_uuid: string;
  pageNo: number;
  phone?: string;
  name?: string;
  itemPerPage?: number;
  pageLimit?: number;
}
export const fetchQuickSendCampaignListAsync =
  (
    params: IFetchQuickSendCampaignListPayload,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      const {
        market_campaign_uuid,
        itemPerPage,
        pageLimit,
        company_outlet_uuid,
        pageNo,
        phone,
        name,
      } = params;
      try {
        dispatch(fetchQuickSendCampaignListProgress());

        let url = `/campaign/get-quick-send-campaign-list?market_campaign_uuid=${market_campaign_uuid}&itemPerPage=${itemPerPage}&pageLimit=${pageLimit}`;

        if (company_outlet_uuid)
          url += `&company_outlet_uuid=${company_outlet_uuid}`;

        if (pageNo) url += `&pageNo=${pageNo}`;

        if (phone) url += `&phone=${phone}`;
        if (name) url += `&name=${name}`;

        const res = await api.get(url);

        // const headers = res.data.data.headers as string[];

        // if (!headers.includes("check")) {
        //   headers = ["check"].concat(headers);
        // }

        dispatch(
          fetchQuickSendCampaignListSuccess(
            res.data.data[0].quick_send_list,
            res.data.data[0].quick_send_list.headers,
            res.data.totalRecords,
          ),
        );
      } catch (err: any) {
        dispatch(fetchQuickSendCampaignListFailed());
        dispatch(
          showMessage({
            type: "error",
            message:
              err.response?.data?.message ||
              "Failed to fetch quick send campaigns",
            displayAs: "snackbar",
          }),
        );
      }
    };


export const runQuickCampaign =
  (
    marketId: string,
    onCallBack: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(saveLoaderProgress());
        await api.post("/campaign/execute-quick-campaign/" + marketId);
        onCallBack(true);

        dispatch(
          showMessage({
            type: "success",
            message: "Campaign has been created successfully.",
            displayAs: "snackbar",
          }),
        );
      } catch (err: any) {
        onCallBack(false);
        dispatch(
          showMessage({
            type: "error",
            message:
              err.response?.data?.message || "Failed to save campaign audience",
            displayAs: "snackbar",
          }),
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const upsertMarketCampaignBasicInfoAsync =
  (
    data: IMarketCampaignBasicInfo,
    onSuccess?: (data: IMarketCampaignBasicInfo) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(saveLoaderProgress());
        const res = await api.post("/campaign/upsert-campaign", data);
        onSuccess?.(res.data.data);
        dispatch(
          showMessage({
            type: "success",
            message: "Campaign basic info saved successfully",
            displayAs: "snackbar",
          }),
        );
      } catch (err: any) {
        dispatch(
          showMessage({
            type: "error",
            message:
              err.response?.data?.message || "Failed to save campaign basic info",
            displayAs: "snackbar",
          }),
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };








export const clearMarketCampaignRuleTemplatesState = () =>
  action(CLEAR_MARKET_CAMPAIGN_RULE_TEMPLATES_STATE);

// Backward-compatible aliases/helpers used by legacy screens.
export const clearMerketCampaignState = () =>
  clearMarketCampaignRuleTemplatesState();

export const fetchSingleMarketCampaignAsync =
  (_marketCampaignUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async () => {};

export const fetchAllMarketCampaignAudienceListAsync =
  (_params: any): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async () => {};

export const fetchMarketCampaignAudienceAsync =
  (_marketCampaignAudienceUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async () => {};

export const upsertMarketCampaignAudienceAsync =
  (_payload: any, onSuccess?: (isSuccess?: boolean) => void): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async () => {
      onSuccess?.(true);
    };

export const saveMarketCampaignAudienceListAsyncV2 =
  (_payload: any, onSuccess?: (isSuccess?: boolean, data?: any) => void): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async () => {
      onSuccess?.(true, undefined);
    };

export const fetchAudienceListColumnsAsync =
  (_companyOutletUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async () => {};

export const upsertAudienceListColumnAsync =
  (
    _columnName: string,
    _companyOutletUuid: string,
    onSuccess?: (updatedData?: any) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async () => {
      onSuccess?.(undefined);
    };

export const saveQuickSendCampaignAsync =
  (_payload: any, onSuccess?: (data?: any) => void): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async () => {
      onSuccess?.(undefined);
    };

export const fetchMarketCampaignCronSchedulersAsync =
  (_marketCampaignUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async () => {};

export const upsertMarketCampaignCronSchedulerAsync =
  (_payload: any, onSuccess?: (isSuccess?: boolean) => void): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async () => {
      onSuccess?.(true);
    };